import type { Editor, TLShape } from 'tldraw'
import { Box } from 'tldraw'

export const CANVAS_WIDTH = 800
export const CANVAS_HEIGHT = 400

const TARGET_ASPECT = CANVAS_WIDTH / CANVAS_HEIGHT
/** Page-space margin around content before aspect-padding. */
const CONTENT_MARGIN_PX = 32

/**
 * Render a CANVAS_WIDTH × CANVAS_HEIGHT JPEG of the current page's content
 * (FS-482). The user's camera, selection, and viewport are never touched —
 * tldraw's `editor.toImage()` builds an SVG off-DOM by calling each shape
 * util's `toSvg()`. Our HTMLContainer shapes (DocPreview / ConnectorCard /
 * CommentPin) all implement `toSvg` via `shapeNodeToSvgImage`, which
 * rasterizes their natural-size DOM. There is no setCamera / rAF / restore
 * dance and therefore no user-visible flicker.
 *
 * Returns `null` when the board is empty or every shape's bounds are
 * unresolved (`getShapePageBounds` returned undefined). The caller treats
 * that as "nothing to capture" and skips the upload.
 */
export async function captureBoardPreview(
  editor: Editor,
  shapes: TLShape[],
): Promise<string | null> {
  if (shapes.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const shape of shapes) {
    const b = editor.getShapePageBounds(shape.id)
    if (!b) continue
    minX = Math.min(minX, b.x)
    minY = Math.min(minY, b.y)
    maxX = Math.max(maxX, b.x + b.w)
    maxY = Math.max(maxY, b.y + b.h)
  }
  if (!isFinite(minX)) return null

  // Inflate the content box by a fixed margin, then aspect-pad it to match
  // the canvas-export ratio. tldraw's own `padding` option would add an
  // additional margin in addition to whatever we pass — we want exact
  // control here, so pre-pad and pass `padding: 0`.
  const marginW = (maxX - minX) + CONTENT_MARGIN_PX * 2
  const marginH = (maxY - minY) + CONTENT_MARGIN_PX * 2
  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2

  let padW: number
  let padH: number
  if (marginW / marginH > TARGET_ASPECT) {
    padW = marginW
    padH = marginW / TARGET_ASPECT
  } else {
    padH = marginH
    padW = marginH * TARGET_ASPECT
  }
  const padX = centerX - padW / 2
  const padY = centerY - padH / 2

  // tldraw applies inline `display: none` to .tl-shape wrappers outside the
  // rendering bounds (cull optimization). shapeNodeToSvgImage rasterizes
  // each custom shape's live DOM, which html-to-image sizes via
  // clientWidth/clientHeight — both zero on a display:none subtree → blank
  // tile in the thumbnail. Force-mount the culled wrappers for the duration
  // of the export (with !important so tldraw's reactor can't race-overwrite
  // before html-to-image reads layout), restore inline style in finally.
  // Greptile P1 on PR #12741.
  const restoreCull = forceMountCulledShapes(editor)
  if (restoreCull.changed) {
    // Yield one frame so the browser flushes layout for the un-hidden
    // wrappers before html-to-image (inside each shape's toSvg) reads
    // their computed dimensions.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  }

  try {
    const { blob } = await editor.toImage(
      shapes.map((s) => s.id),
      {
        bounds: new Box(padX, padY, padW, padH),
        padding: 0,
        background: true,
        format: 'jpeg',
        quality: 0.8,
        // output pixel width = padW * scale * pixelRatio
        scale: CANVAS_WIDTH / padW,
        pixelRatio: 1,
      },
    )
    return await blobToDataUrl(blob)
  } finally {
    restoreCull()
  }
}

/**
 * Override `display: none` on culled `.tl-shape` wrappers inside the editor's
 * container so html-to-image (inside each shape's toSvg) can read non-zero
 * layout dimensions. Returns a restore callable that puts the inline style
 * back exactly as it was, plus a `.changed` flag so the caller can skip the
 * rAF yield when nothing was culled.
 */
function forceMountCulledShapes(
  editor: Pick<Editor, 'getContainer'>,
): { (): void; changed: boolean } {
  let root: HTMLElement | null = null
  try {
    root = editor.getContainer()
  } catch {
    const noop = () => {}
    return Object.assign(noop, { changed: false })
  }
  if (!root) {
    const noop = () => {}
    return Object.assign(noop, { changed: false })
  }

  const overrides: { el: HTMLElement; cssText: string }[] = []
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('.tl-shape'))) {
    if (el.style.display === 'none') {
      overrides.push({ el, cssText: el.style.cssText })
      el.style.setProperty('display', 'block', 'important')
    }
  }

  const restore = () => {
    for (const { el, cssText } of overrides) {
      el.style.cssText = cssText
    }
  }
  return Object.assign(restore, { changed: overrides.length > 0 })
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}
